// src/utils/noteHandler.ts

import axios from 'axios';
import { LEGACY_API } from '../config/api';

const API_URL = LEGACY_API.NOTES;

interface NoteArgs {
    userSub?: string;
    content?: string;
    noteID?: string;
    parentNoteID?: string;
    referenceType?: string;
    referenceTargetRlBookId?: number;
    referenceTargetClassLevel?: string;
    referenceSourceRlBookId?: number;
    referenceSourceClassLevel?: string;
    referenceTargetTENKYO_ID?: string;
    additionalTENKYO_ID?: string;
    additionalTENKYO_type?: string;
    metaDescription?: string;
    noteOpenLevel?: string;
    allowReplyLevel?: string;
}

interface HandleNoteOperationArgs extends NoteArgs {
    Operation:
    'CreateExplainNote'
    | 'EditExplainNote'
    | 'DeleteExplainNote'
    | 'CreateImpressionNote'
    | 'EditImpressionNote'
    | 'DeleteImpressionNote'
    | 'CreateInformationNote'
    | 'EditInformationNote'
    | 'DeleteInformationNote'
    | 'GetNote'
    | 'GetNoteOpenEndpoint'
}

const removeNullValues = (obj: any) => {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
};

// API に送信する際、rl_book_id キー名に変換
const buildPayload = (operation: string, args: NoteArgs) => {
    return removeNullValues({
        Operation: operation,
        ...args,
    });
};

const callNoteApi = async (operation: string, args: NoteArgs, idToken: string) => {
    const data = buildPayload(operation, args);
    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
    };

    try {
        const response = await axios.post(API_URL, data, { headers });
        return response.data;
    } catch (error) {
        console.error(`Note operation ${operation} failed:`, error);
        return null;
    }
};

const CreateExplainNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('CreateExplainNote', args, idToken);
};

const EditExplainNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('EditExplainNote', args, idToken);
};

const DeleteExplainNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('DeleteExplainNote', args, idToken);
};

const CreateImpressionNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('CreateImpressionNote', args, idToken);
};

const EditImpressionNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('EditImpressionNote', args, idToken);
};

const DeleteImpressionNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('DeleteImpressionNote', args, idToken);
};

const CreateInformationNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('CreateInformationNote', args, idToken);
};

const EditInformationNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('EditInformationNote', args, idToken);
};

const DeleteInformationNote = async (args: NoteArgs, idToken: string) => {
    return callNoteApi('DeleteInformationNote', args, idToken);
};

const GetNote = async (args: NoteArgs, idToken?: string) => {
    const data = {
        Operation: 'GetNote',
        ...args
    };

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
    }

    try {
        const response = await axios.post(API_URL, data, { headers });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch note detail:', error);
        return null;
    }
};

const GetNoteOpenEndpoint = async (args: NoteArgs) => {
    const data = {
        Operation: 'GetNoteOpenEndpoint',
        ...args
    };

    const headers = {
        'Content-Type': 'application/json',
    };

    const API_URL_OPEN = LEGACY_API.NOTES_OPEN;

    try {
        const response = await axios.post(API_URL_OPEN, data, { headers });
        return response.data;
    } catch (error) {
        console.error('Failed to fetch note detail:', error);
        return null;
    }
};

const handleNoteOperation = async (args: HandleNoteOperationArgs, idToken: string) => {
    const { Operation, ...rest } = args;
    switch (Operation) {
        case 'CreateExplainNote':
            return await CreateExplainNote(rest, idToken);
        case 'EditExplainNote':
            return await EditExplainNote(rest, idToken);
        case 'DeleteExplainNote':
            return await DeleteExplainNote(rest, idToken);
        case 'CreateImpressionNote':
            return await CreateImpressionNote(rest, idToken);
        case 'EditImpressionNote':
            return await EditImpressionNote(rest, idToken);
        case 'DeleteImpressionNote':
            return await DeleteImpressionNote(rest, idToken);
        case 'CreateInformationNote':
            return await CreateInformationNote(rest, idToken);
        case 'EditInformationNote':
            return await EditInformationNote(rest, idToken);
        case 'DeleteInformationNote':
            return await DeleteInformationNote(rest, idToken);
        case 'GetNote':
            return await GetNote(rest, idToken);
        case 'GetNoteOpenEndpoint':
            return await GetNoteOpenEndpoint(rest);
        default:
            throw new Error(`Unknown Operation: ${Operation}`);
    }
};

export {
    handleNoteOperation,
    CreateExplainNote,
    EditExplainNote,
    DeleteExplainNote,
    CreateImpressionNote,
    EditImpressionNote,
    DeleteImpressionNote,
    CreateInformationNote,
    EditInformationNote,
    DeleteInformationNote,
    GetNote,
    GetNoteOpenEndpoint
};
